import React, { useEffect, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import Boton from './Boton';
import CampoMonto from './CampoMonto';
import { conMiles } from './formatos';
import '../../styles/components/EditarFila.css';

const comoTexto = (valor) => (valor === null || valor === undefined ? '' : String(valor));

const formatearTasa = (valor) => Number(valor).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

// Opciones de una lista de códigos; un valor actual que no está en la lista también aparece
function opcionesDe(lista, valorActual) {
    const opciones = Object.entries(lista).map(([codigo, nombre]) => ({ valor: codigo, texto: `${codigo} · ${nombre}` }));
    if (valorActual !== '' && !lista[valorActual]) {
        opciones.unshift({ valor: valorActual, texto: `${valorActual} · no es un código válido` });
    }
    return opciones;
}

/**
 * Formulario para editar una fila de una carga del módulo Inversiones exterior.
 * Muestra las 22 columnas de la DJ 1929 con sus valores y los avisos de la fila.
 * Editar es opcional: se guarda lo que escribe el contador, aunque tenga avisos,
 * y el servidor vuelve a revisarla al guardar. En una fila en otra moneda (PDF, …)
 * los montos se escriben en esa moneda y el servidor los pasa a pesos con la tasa.
 *
 * @param {Object} props
 * @param {string} props.titulo - Título del formulario (editar una línea o agregar una fila)
 * @param {Object} props.fila - Fila a editar; para agregar, una fila vacía
 * @param {Object[]} props.columnas - Columnas de la DJ 1929 (campo, titulo, tipo, largo, monto)
 * @param {Object} props.codigos - Códigos de operación (código: nombre)
 * @param {Object} props.relaciones - Códigos de relación (código: nombre)
 * @param {Object[]|null} props.paises - Países (sigla, nombre); null mientras cargan
 * @param {Function} props.onGuardar - Recibe los valores del formulario
 * @param {Function} props.onCerrar - Cierra sin guardar
 */
const EditarFila = ({ titulo, fila, columnas, codigos, relaciones, paises, onGuardar, onCerrar }) => {
    const otraMoneda = Boolean(fila.moneda) && fila.moneda !== 'CLP';
    const [valores, setValores] = useState(() =>
        Object.fromEntries(columnas.map((columna) => [
            columna.campo,
            comoTexto(otraMoneda && columna.monto ? fila.montos_origen?.[columna.campo] : fila[columna.campo])
        ]))
    );
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        const alPresionar = (e) => {
            if (e.key === 'Escape' && !guardando) onCerrar();
        };
        document.addEventListener('keydown', alPresionar);
        return () => document.removeEventListener('keydown', alPresionar);
    }, [guardando, onCerrar]);

    const cambiar = (campo, valor) => setValores((actuales) => ({ ...actuales, [campo]: valor }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGuardando(true);
        try {
            await onGuardar(valores);
        } finally {
            setGuardando(false);
        }
    };

    const control = (columna) => {
        const valor = valores[columna.campo];
        if (columna.monto) {
            return (
                <CampoMonto
                    valor={valor}
                    largo={columna.largo}
                    decimales={otraMoneda ? 2 : 0}
                    onCambiar={(monto) => cambiar(columna.campo, monto)}
                />
            );
        }
        if (columna.campo === 'operacion' || columna.campo === 'relacion') {
            const lista = columna.campo === 'operacion' ? codigos : relaciones;
            return (
                <select value={valor} onChange={(e) => cambiar(columna.campo, e.target.value)}>
                    <option value="">—</option>
                    {opcionesDe(lista, valor).map((opcion) => (
                        <option key={opcion.valor} value={opcion.valor}>{opcion.texto}</option>
                    ))}
                </select>
            );
        }
        if (columna.campo === 'pais') {
            const lista = paises ?? [];
            const fueraDeLista = valor !== '' && !lista.some((pais) => pais.sigla === valor);
            return (
                <select value={valor} onChange={(e) => cambiar('pais', e.target.value)} disabled={!paises}>
                    <option value="">—</option>
                    {fueraDeLista && <option value={valor}>{valor}</option>}
                    {lista.map((pais) => (
                        <option key={pais.sigla} value={pais.sigla}>{pais.sigla} · {pais.nombre}</option>
                    ))}
                </select>
            );
        }
        return (
            <input
                value={valor}
                onChange={(e) => cambiar(columna.campo, e.target.value)}
                inputMode={columna.tipo === 'N' ? 'numeric' : columna.tipo === 'P' ? 'decimal' : undefined}
            />
        );
    };

    const campo = (columna) => (
        <label key={columna.campo}>
            {columna.titulo}
            {control(columna)}
            {columna.tipo === 'C' && columna.campo !== 'pais' && (
                <small className={valores[columna.campo].length > columna.largo ? 'excede' : ''}>
                    {valores[columna.campo].length} de {columna.largo} caracteres para el SII
                </small>
            )}
            {otraMoneda && columna.monto && fila.tasa_valor && enPesos(valores[columna.campo]) !== null && (
                <small>≈ ${conMiles(enPesos(valores[columna.campo]))}</small>
            )}
        </label>
    );

    // Vista previa en pesos de un monto en otra moneda; el servidor calcula el valor que se guarda
    function enPesos(monto) {
        const numero = Number(monto);
        return monto === '' || monto === '-' || !Number.isFinite(numero) ? null : Math.round(numero * Number(fila.tasa_valor));
    }

    return (
        <div className="invext-ef-fondo">
            <div className="invext-ef-modal" role="dialog" aria-modal="true" aria-labelledby="invext-ef-titulo">
                <form onSubmit={handleSubmit}>
                    <div className="invext-ef-cabecera">
                        <h2 id="invext-ef-titulo">{titulo}</h2>
                        <p>
                            Se guarda lo que escribas, aunque tenga avisos; al guardar se vuelve a revisar.
                            {otraMoneda
                                ? ` Los montos van en ${fila.moneda}, con coma decimal; los puntos de miles se ponen solos.`
                                : ' Los montos van en pesos enteros; los puntos de miles se ponen solos.'}
                        </p>
                    </div>

                    {fila.avisos.length > 0 && (
                        <ul className="invext-ef-avisos">
                            {fila.avisos.map((aviso, indice) => (
                                <li key={indice} className={aviso.nivel}>{aviso.mensaje}</li>
                            ))}
                        </ul>
                    )}

                    <fieldset>
                        <legend>Entidad extranjera</legend>
                        <div className="invext-ef-campos">{columnas.filter((c) => !c.monto).map(campo)}</div>
                    </fieldset>

                    <fieldset>
                        <legend>{otraMoneda ? `Montos en ${fila.moneda}` : 'Montos en pesos'}</legend>
                        {otraMoneda && (
                            <p className="invext-ef-tasa">
                                {fila.tasa_valor
                                    ? `Se pasan a pesos con la tasa ${fila.moneda} al 31-12: $${formatearTasa(fila.tasa_valor)}.`
                                    : `Sin tasa ${fila.moneda} al 31-12: la registra EFE; mientras tanto la fila no tiene pesos.`}
                            </p>
                        )}
                        <div className="invext-ef-campos">{columnas.filter((c) => c.monto).map(campo)}</div>
                    </fieldset>

                    <div className="invext-botones invext-ef-acciones">
                        <Boton onClick={onCerrar} disabled={guardando}>Cancelar</Boton>
                        <Boton type="submit" variante="primario" icono={<FaCheck />} disabled={guardando}>
                            {guardando ? 'Guardando...' : 'Guardar'}
                        </Boton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditarFila;
